import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

const AnalysisAccordion = ({
  title,
  fields,
  buttons,
  disabled = false,
  tooltip = '',
}) => {
  
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 300, // Set max height for the dropdown
        overflow: 'auto', // Enable scrolling
      },
    },
  };

  return (
    <Box
      sx={{
        boxShadow: 1,
        borderRadius: 1,
        bgcolor: 'background.paper',
        p: 1,
        '&:hover': {
          cursor: disabled ? 'not-allowed' : 'default',
        },
      }}
    >
      <Tooltip
        title={disabled ? tooltip : ''}
        placement="bottom"
        arrow
        className={`${disabled?'cursor-not-allowed':''}`}
        disableInteractive
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            opacity: disabled ? 0.5 : 1,
            paddingY: 0.5,
            cursor: disabled ? 'not-allowed' : 'default',
            backgroundColor: 'transparent !important',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              flex: '0 0 auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {title}
          </Typography>

          {/* Input Fields */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flex: 1,
              ml: -0.5, // Shift left by 4px (0.5 * 8px = 4px)
            }}
          >

            {/* Update this so it can take a custom component input for compsearch (or just do as a one off - keep it outlined tho) */}
            {fields.map((field, index) => (
              <FormControl
                key={index}
                size="small"
                sx={{
                  flex: field.minWidth ? '0 0 auto' : 1,
                  maxWidth: field.width || '100%', // Optionally cap the width (if needed)
                  ...(field.minWidth ? { minWidth: field.minWidth } : {}),
                  visibility: field.visible === false ? 'hidden' : 'visible',
                  position: 'relative',
                }}
                error={!!field.error} // Display error if field.error is truthy
              >
                {field.type === 'select' ? (
                  <>
                    <InputLabel
                      sx={{ bgcolor: 'white !important', zIndex: '1 !important', pointerEvents: 'none' }}
                    >
                      {field.label}
                    </InputLabel>
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      MenuProps={menuProps}
                      disabled={!!field.disabled}
                    >
                      {field.options.map((option, i) => {
                        const menuItem = (
                          <MenuItem 
                            key={i} 
                            value={option.value} 
                            disabled={!!option.disabled}
                          >
                            {option.label}
                          </MenuItem>
                        );
                        
                        if (option.tooltip && option.disabled) {
                          return (
                            <Tooltip
                              key={i}
                              title={option.tooltip}
                              placement="right"
                              arrow
                              disableInteractive
                              PopperProps={{
                                style: { zIndex: 1300 },
                              }}
                            >
                              <span style={{ display: 'flex', width: '100%' }}>
                                {menuItem}
                              </span>
                            </Tooltip>
                          );
                        }
                        
                        return menuItem;
                      })}
                    </Select>
                  </>
                ) : field.type === 'custom' ? (
                  // Custom component rendering
                  <Box sx={{ width: '100%' }}>
                    {field.component}
                  </Box>
                ) : (
                  <TextField
                    size="small"
                    label={field.label}
                    value={field.value}
                    onChange={field.onChange}
                    sx={{
                      // width: 'fit-content', // Adjust width to fit content
                      // minWidth: '160px', // Ensure a minimum width
                      // maxWidth: field.width || '100%', // Optionally cap the width (if needed)
                    }}
                    error={!!field.error} // Error styling for the TextField
                  />
                )}
                {field.error && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      position: 'absolute',
                      bottom: '-20px', // Adjust as needed to position error text below the input
                      left: '50%', // Align to the center of the parent
                      transform: 'translateX(-50%)', // Adjust alignment for exact centering
                      textAlign: 'center', // Ensure text is centered
                      whiteSpace: 'nowrap', // Prevent wrapping
                    }}
                  >
                    {field.error} {/* Display the error text */}
                  </Typography>
                )}
              </FormControl>
            ))}
          </Box>

          {/* Buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end', alignItems: 'center' }} >
            {buttons.map((button, index) => {
              if (button.type === 'tooltip') {
                return (
                  <Tooltip key={index} title={button.tooltip} arrow>
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      {button.children}
                    </Box>
                  </Tooltip>
                );
              }
              if (button.type === 'icon') {
                return (
                  <Tooltip title={disabled ? '' : button.tooltip} key={index} disableInteractive>
                    <Box
                      onClick={(e) => {
                        if (!disabled) button.onClick();
                      }}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        p: 0.5,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        '&:focus': { outline: 'none' },
                        pointerEvents: disabled ? 'none' : 'auto',
                        color: 'action.active',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      <SettingsOutlinedIcon />
                    </Box>
                  </Tooltip>
                );
              }
              return (
                <Box
                  key={index}
                  onClick={(e) => {
                    if (!disabled) button.onClick();
                  }}
                  disabled={button.disabled}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    boxSizing: 'border-box',
                    minWidth: 64,
                    padding: '6px 16px',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    lineHeight: 1.75,
                    letterSpacing: '0.02857em',
                    textTransform: 'uppercase',
                    cursor: button.disabled ? 'default' : 'pointer',
                    backgroundColor: button.variant === 'contained' ? 'primary.main' : 'transparent',
                    color: button.variant === 'contained' ? 'primary.contrastText' : 'primary.main',
                    border: button.variant === 'outlined' ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
                    '&:hover': {
                      backgroundColor: button.variant === 'contained' ? 'primary.dark' : 'rgba(0, 0, 0, 0.04)',
                    },
                    opacity: button.disabled ? 0.5 : 1,
                    pointerEvents: button.disabled ? 'none' : 'auto',
                  }}
                >
                  {!button.endIcon && button.label}
                  {button.endIcon && (
                    <Box component="span">
                      {button.endIcon}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export default AnalysisAccordion;
